import { useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useSpaces } from "../../context/SpaceProvider";
import { useProjectIndex } from "../../hooks/useProjectIndex";
import { queryClient } from "../../lib/queryClient";
import { precheckDistress } from "../../lib/distressPrecheck";
import { normalizeLocale } from "../../lib/startFlow";
import { trackCapture } from "../../lib/captureAnalytics";
import { NEW_PROJECT, initSheet, sheetReducer, toCommitBody, type SheetTarget } from "../../lib/captureSheet";
import {
  CAPTURE_DUES,
  CAPTURE_KINDS,
  MM_TYPES,
  commitCapture,
  isDistress,
  isFallback,
  understandCapture,
  type CaptureDue,
  type CaptureItem,
  type CaptureKind,
  type CommitResponse,
  type UnderstandSource,
} from "../../api/capture";
import type { MmObjectType } from "../../api/mentalModels";
import SupportScreen from "../start/SupportScreen";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { AddChip, ChipSelect } from "./ChipSelect";
import BodySuggestion from "./BodySuggestion";
import DiveDeeperCard, { type SavedItem } from "./DiveDeeperCard";
import type { DeeperItem } from "../../lib/diveDeeper";
import NowSession from "../now/NowSession";
import type { NowMode } from "../../api/now";

export interface CaptureSheetProps {
  text: string;
  source: UnderstandSource;
  onClose: () => void;
  onSaved?: (result: CommitResponse) => void;
}

type DueOption = CaptureDue | "none";
const DUE_OPTIONS: DueOption[] = ["none", ...CAPTURE_DUES];
const UNDERSTAND_TIMEOUT_MS = 12000;
const TITLE_CHIP_MAX = 40;
const INVALIDATE_KEYS = ["home-today-actions", "lifeos-roots", "cockpit-projects"];

const Words = ({ text }: { text: string }) => (
  <p dir="auto" className="text-sm leading-relaxed whitespace-pre-wrap text-start max-h-40 overflow-y-auto">
    {text}
  </p>
);

/**
 * Smart capture sheet (spec 2026-11-02 §3): the whole text is saved as it is
 * (one item, kind changeable); parts the AI found are unticked suggestions.
 */
const CaptureSheet = ({ text, source, onClose, onSaved }: CaptureSheetProps) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const { spaces } = useSpaces();
  const { projectById } = useProjectIndex();
  const locale = normalizeLocale(i18n.language);
  const [state, dispatch] = useReducer(sheetReducer, text, initSheet);
  const [savedFromDistress, setSavedFromDistress] = useState(false);
  const [nowStart, setNowStart] = useState<{ captureId: string; mode: NowMode | null; item: DeeperItem; reflective: boolean } | null>(null);
  const started = useRef(false);

  const settled = useRef(false);

  useEffect(() => {
    const offline = {
      source,
      wholeKind: null,
      partsFound: 0,
      reflective: false,
      ai: false,
    };
    if (!started.current) {
      started.current = true;
      // Same keyword screen as the server, so support shows even when offline.
      if (precheckDistress(state.text)) {
        settled.current = true;
        dispatch({ type: "understood", response: { distress: true } });
        return;
      }
      understandCapture(retryFetch, { text: state.text, locale, source })
        .then((response) => {
          if (settled.current) return;
          settled.current = true;
          dispatch({ type: "understood", response });
          if (isFallback(response)) trackCapture("capture_understood", offline);
          else if (!isDistress(response)) {
            trackCapture("capture_understood", {
              source,
              wholeKind: response.whole.kind,
              partsFound: response.parts.length,
              reflective: response.reflective,
              ai: true,
            });
          }
        })
        .catch(() => {
          if (settled.current) return;
          settled.current = true;
          dispatch({ type: "fail" });
          trackCapture("capture_understood", offline);
        });
    }
    // Don't leave the person staring at a spinner if the AI hangs.
    if (settled.current) return;
    const timer = setTimeout(() => {
      if (settled.current) return;
      settled.current = true;
      dispatch({ type: "fail" });
      trackCapture("capture_understood", offline);
    }, UNDERSTAND_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (overrideKind?: CaptureKind) => {
    const fromDistress = state.phase === "distress";
    dispatch({ type: "saving" });
    try {
      const result = await commitCapture(retryFetch, toCommitBody(state, { source, locale, overrideKind, fromDistress }));
      setSavedFromDistress(fromDistress);
      dispatch({ type: "saved", result });
      trackCapture("capture_saved", {
        source,
        wholeKind: result.items[0]?.kind ?? null,
        partsTicked: Math.max(0, result.items.length - 1),
      });
      INVALIDATE_KEYS.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
      onSaved?.(result);
    } catch {
      dispatch({ type: "saveFailed" });
    }
  };

  const changeKind = (target: SheetTarget, kind: CaptureKind) => {
    const from = target === "whole" ? state.whole.kind : state.parts[target]?.kind;
    if (from === kind) return;
    dispatch({ type: "setKind", target, kind });
    trackCapture("capture_kind_changed", { from: from ?? null, to: kind });
  };

  const kindLabel = (k: CaptureKind) => t(`capture.kind.${k}`);
  const mmLabel = (m: MmObjectType) => t(`capture.mmType.${m}`);
  const dueLabel = (d: DueOption) => (d === "none" ? t("capture.noDate") : t(`capture.due.${d}`));
  // An id not (yet) in the cache (cold /capture share) still gets a chip with
  // the generic label, so it stays visible and removable.
  const spaceName = (id: string): string => spaces.find((s) => s._id === id)?.name || t("capture.spaceLabel");
  const projectTitle = (id: string): string => {
    if (id === NEW_PROJECT) return t("capture.newProjectChip");
    const title = projectById[id]?.title;
    if (!title) return t("capture.projectLabel");
    // Captured projects are titled with the person's whole note; keep chips short.
    return title.length > TITLE_CHIP_MAX ? `${title.slice(0, TITLE_CHIP_MAX - 1).trimEnd()}…` : title;
  };
  const spaceIds = spaces.map((s) => s._id);
  // Journeys without a life project can't take captures (the server checks the same).
  const projectIds = Object.values(projectById)
    .filter((p) => p.isLifeProject)
    .map((p) => p.id);

  /** Space and project chips for one item: change, remove, or add when the AI left it unfiled. */
  const filingChips = (item: CaptureItem, target: SheetTarget) => {
    const isPart = target !== "whole";
    const projectValue = item.inNewProject ? NEW_PROJECT : item.projectId;
    const projectOptions = isPart && state.whole.kind === "project" ? [NEW_PROJECT, ...projectIds] : projectIds;
    const canHaveProject = item.kind !== "project" && item.kind !== "mental_model";
    const pickProject = (id: string) => {
      dispatch({ type: "setProject", target, projectId: id });
      // A project already lives in an area: file the note there too unless one is chosen.
      const area = projectById[id]?.spaceId;
      if (!item.spaceId && area && spaceIds.includes(area)) dispatch({ type: "setSpace", target, spaceId: area });
    };
    const nudge = !isPart && item.kind === "project" && !item.spaceId && spaceIds.length > 0;
    return (
      <>
        {item.spaceId ? (
          <ChipSelect
            value={item.spaceId}
            options={spaceIds}
            label={spaceName}
            onChange={(id) => dispatch({ type: "setSpace", target, spaceId: id })}
            onRemove={() => dispatch({ type: "setSpace", target, spaceId: null })}
            removeLabel={t("capture.remove")}
            ariaLabel={t("capture.spaceLabel")}
            disabled={busy}
          />
        ) : (
          spaceIds.length > 0 && (
            <AddChip
              text={t("capture.addSpace")}
              options={spaceIds}
              label={spaceName}
              onChange={(id) => dispatch({ type: "setSpace", target, spaceId: id })}
              ariaLabel={t("capture.addSpace")}
              disabled={busy}
              highlight={nudge}
            />
          )
        )}
        {canHaveProject &&
          (projectValue ? (
            <ChipSelect
              value={projectValue}
              options={projectOptions}
              label={projectTitle}
              onChange={pickProject}
              onRemove={() => dispatch({ type: "setProject", target, projectId: null })}
              removeLabel={t("capture.remove")}
              ariaLabel={t("capture.projectLabel")}
              disabled={busy}
            />
          ) : (
            projectOptions.length > 0 && (
              <AddChip
                text={t("capture.addProject")}
                options={projectOptions}
                label={projectTitle}
                onChange={pickProject}
                ariaLabel={t("capture.addProject")}
                disabled={busy}
              />
            )
          ))}
        {nudge && <p className="basis-full text-xs text-primary/80">{t("capture.spaceNudge")}</p>}
      </>
    );
  };

  const busy = state.phase === "saving";
  const view = busy ? state.returnTo : state.phase;
  const errorLine = state.error ? <p className="text-xs text-destructive">{t("capture.saveError")}</p> : null;
  const closeButton = (
    <Button variant="ghost" onClick={onClose}>
      {t("capture.close")}
    </Button>
  );

  if (nowStart) {
    return (
      <NowSession
        captureId={nowStart.captureId}
        mode={nowStart.mode}
        item={nowStart.item}
        reflective={nowStart.reflective}
        onClose={onClose}
      />
    );
  }

  if (state.phase === "understanding") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground" role="status">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("capture.understanding")}
        </div>
        <div className="flex justify-end">{closeButton}</div>
      </div>
    );
  }

  if (state.phase === "saved" && state.result) {
    if (savedFromDistress) {
      return (
        <div className="space-y-4">
          <p className="text-sm font-medium text-foreground" role="status">
            {t("capture.distress.savedForLater")}
          </p>
          <div className="flex justify-end">{closeButton}</div>
        </div>
      );
    }
    const { captureId } = state.result;
    const ticked = state.parts.filter((p) => p.ticked);
    const items: SavedItem[] = state.result.items.map((it, i) => {
      const src = i === 0 ? state.whole : ticked[i - 1];
      return { ...it, mmType: it.mmType ?? src?.mmType, text: src?.text ?? "" };
    });
    return (
      <div className="space-y-4">
        <DiveDeeperCard
          items={items}
          mode={state.mode}
          modeWhy={state.modeWhy}
          suggestedMode={state.suggestedMode}
          onModeChange={(m) => dispatch({ type: "setMode", mode: m })}
          onDive={(it, index) =>
            setNowStart({
              captureId,
              mode: state.mode,
              item: { kind: it.kind, id: it.id, ...(it.mmType ? { mmType: it.mmType } : {}) },
              // `reflective` describes the whole note only.
              reflective: state.reflective && index === 0,
            })
          }
          onNotNow={onClose}
        />
        {state.body && <BodySuggestion body={state.body} currentCaptureId={captureId} locale={locale} />}
      </div>
    );
  }

  if (view === "distress") {
    return (
      <div className="space-y-5">
        <SupportScreen locale={locale} />
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => save("insight")}
            className="text-sm underline text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {t("capture.distress.saveForLater")}
          </button>
          {closeButton}
        </div>
        {errorLine}
      </div>
    );
  }

  if (view === "fallback") {
    return (
      <div className="space-y-4">
        <Words text={state.text} />
        <div>
          <p className="text-sm font-semibold">{t("capture.chooseKind")}</p>
          <p className="text-xs text-muted-foreground">{t("capture.fallbackHint")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {CAPTURE_KINDS.map((k) => (
            <Button
              key={k}
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={busy}
              onClick={() => save(k)}
            >
              {kindLabel(k)}
            </Button>
          ))}
        </div>
        {errorLine}
        <div className="flex justify-end">{closeButton}</div>
      </div>
    );
  }

  // review
  const { whole } = state;
  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-primary/20 bg-primary/[0.03] p-4 space-y-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
            {t("capture.saveAsIs")}
          </p>
          <p className="text-xs text-muted-foreground">{t("capture.saveAsIsHint")}</p>
        </div>
        <Words text={state.text} />
        <div className="flex flex-wrap items-start gap-1.5">
          <ChipSelect
            tone="primary"
            value={whole.kind}
            options={CAPTURE_KINDS}
            label={kindLabel}
            onChange={(k) => changeKind("whole", k)}
            ariaLabel={t("capture.kindLabel")}
            disabled={busy}
          />
          {whole.kind === "mental_model" && (
            <ChipSelect
              value={whole.mmType ?? "other"}
              options={MM_TYPES}
              label={mmLabel}
              onChange={(m) => dispatch({ type: "setMmType", target: "whole", mmType: m })}
              ariaLabel={t("capture.mmTypeLabel")}
              disabled={busy}
            />
          )}
          {whole.kind === "action" && (
            <ChipSelect
              value={whole.due ?? "none"}
              options={DUE_OPTIONS}
              label={dueLabel}
              onChange={(d) => dispatch({ type: "setDue", due: d === "none" ? null : d })}
              ariaLabel={t("capture.dueLabel")}
              disabled={busy}
            />
          )}
          {filingChips(whole, "whole")}
        </div>
        {state.projectMatch && whole.projectId === state.projectMatch && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-primary/[0.06] px-3 py-2">
            <p className="text-xs text-foreground/80">
              {t("capture.projectMatch", { title: projectTitle(state.projectMatch) })}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => dispatch({ type: "newProjectInstead" })}
              className="text-xs underline text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              {t("capture.newProjectInstead")}
            </button>
          </div>
        )}
      </section>

      {state.parts.length > 0 && (
        <section className="space-y-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
              {t("capture.alsoFound")}
            </p>
            <p className="text-xs text-muted-foreground">{t("capture.alsoFoundHint")}</p>
          </div>
          {state.parts.map((p, i) => (
            <div
              key={i}
              className={cn(
                "flex items-start gap-3 rounded-xl border border-border px-3 py-2",
                !p.ticked && "opacity-75"
              )}
            >
              <Checkbox
                checked={p.ticked}
                onCheckedChange={() => dispatch({ type: "togglePart", index: i })}
                aria-label={p.text}
                disabled={busy}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0 space-y-1.5">
                <p dir="auto" className="text-sm text-start">
                  {p.text}
                </p>
                <div className="flex flex-wrap items-start gap-1.5">
                  <ChipSelect
                    value={p.kind}
                    options={CAPTURE_KINDS}
                    label={kindLabel}
                    onChange={(k) => changeKind(i, k)}
                    ariaLabel={t("capture.kindLabel")}
                    disabled={busy}
                  />
                  {p.kind === "mental_model" && (
                    <ChipSelect
                      value={p.mmType ?? "other"}
                      options={MM_TYPES}
                      label={mmLabel}
                      onChange={(m) => dispatch({ type: "setMmType", target: i, mmType: m })}
                      ariaLabel={t("capture.mmTypeLabel")}
                      disabled={busy}
                    />
                  )}
                  {filingChips(p, i)}
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {errorLine}
      <div className="flex items-center justify-end gap-2">
        {closeButton}
        <Button className="rounded-full px-5" disabled={busy} onClick={() => save()}>
          {busy && <Loader2 className="w-4 h-4 me-1.5 animate-spin" />}
          {t("capture.save")}
        </Button>
      </div>
    </div>
  );
};

export default CaptureSheet;
